import type { ReactNode } from 'react';

/**
 * TCR-2664 — làm nổi các từ khoá người dùng đã gõ trong 1 đoạn chữ (tên SP, tên combo...).
 *
 * - So khớp KHÔNG phân biệt hoa/thường và KHÔNG phân biệt dấu tiếng Việt ("ao thun" làm nổi
 *   "Áo Thun"), cùng quy tắc mà BE dùng khi tìm.
 * - Từ khoá được tách theo khoảng trắng; mỗi từ được làm nổi độc lập (khớp kiểu "vài từ rời").
 * - Chỉ thêm thẻ <mark>; khi không có từ khoá/không khớp thì trả nguyên chuỗi để không đổi DOM cũ.
 */

/** Chuẩn hoá 1 ký tự: bỏ dấu, hạ chữ thường, đ→d. Luôn trả về chuỗi (có thể dài > 1 với ký tự lạ). */
function foldChar(ch: string): string {
  if (ch === 'đ' || ch === 'Đ') return 'd';
  return ch.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * Trả về chuỗi đã chuẩn hoá kèm bảng ánh xạ vị trí chuẩn hoá → vị trí gốc, để khoảng khớp trên
 * chuỗi chuẩn hoá quy ngược được về đúng đoạn chữ gốc (độ dài hai chuỗi có thể lệch nhau).
 */
function foldWithMap(text: string): { folded: string; map: number[] } {
  let folded = '';
  const map: number[] = [];
  for (let i = 0; i < text.length; i += 1) {
    const f = foldChar(text[i]);
    for (let k = 0; k < f.length; k += 1) {
      folded += f[k];
      map.push(i);
    }
  }
  return { folded, map };
}

/** Tách từ khoá: bỏ dấu, hạ thường, bỏ trùng, bỏ từ rỗng. */
export function splitKeywordTokens(keyword: string): string[] {
  const tokens = foldWithMap(keyword.trim()).folded.split(/\s+/).filter(Boolean);
  return Array.from(new Set(tokens));
}

/** Tính các khoảng [start, end) trên chuỗi GỐC cần làm nổi (đã gộp khoảng chồng nhau). */
export function computeHighlightRanges(text: string, keyword: string): Array<[number, number]> {
  const tokens = splitKeywordTokens(keyword);
  if (!text || tokens.length === 0) return [];
  const { folded, map } = foldWithMap(text);
  const raw: Array<[number, number]> = [];
  for (const token of tokens) {
    let from = 0;
    while (from <= folded.length - token.length) {
      const at = folded.indexOf(token, from);
      if (at === -1) break;
      raw.push([map[at], map[at + token.length - 1] + 1]);
      from = at + token.length;
    }
  }
  raw.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const r of raw) {
    const last = merged[merged.length - 1];
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]);
    else merged.push([r[0], r[1]]);
  }
  return merged;
}

interface Props {
  text: string;
  keyword?: string;
}

export function HighlightText({ text, keyword }: Props): JSX.Element {
  const ranges = keyword ? computeHighlightRanges(text, keyword) : [];
  if (ranges.length === 0) return <>{text}</>;
  const parts: ReactNode[] = [];
  let cursor = 0;
  ranges.forEach(([start, end], idx) => {
    if (start > cursor) parts.push(text.slice(cursor, start));
    parts.push(
      <mark
        key={`${start}-${idx}`}
        style={{ background: '#ffe58f', color: 'inherit', padding: 0, borderRadius: 2 }}
      >
        {text.slice(start, end)}
      </mark>,
    );
    cursor = end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <>{parts}</>;
}
